import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { staffXpApi, staffAuth } from "@/lib/experienceApi";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ScanLine, CheckCircle2, XCircle, LogOut, Type } from "lucide-react";

export default function StaffScanner() {
  const [ctx, setCtx] = useState(staffAuth.getCtx());
  const [eventId, setEventId] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const [manualToken, setManualToken] = useState("");
  const [showManual, setShowManual] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (ctx) staffXpApi.me().catch(() => { staffAuth.clear(); setCtx(null); });
  }, [ctx]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await staffXpApi.login(eventId.trim(), pin.trim());
      staffAuth.setToken(res.session_token);
      staffAuth.setCtx(res.staff);
      setCtx(res.staff);
      toast.success("Bienvenue " + (res.staff.name || "Serveur"));
    } catch (e) { toast.error(e?.response?.data?.detail || "PIN invalide"); }
    finally { setBusy(false); }
  };

  const doScan = async (token) => {
    if (!token) return;
    setBusy(true);
    try {
      const res = await staffXpApi.scan(token);
      setLastScan({ ok: true, wine: res.wine, points: res.points_awarded, remaining: res.doses_remaining });
      // Vibrate on success
      if (navigator.vibrate) navigator.vibrate(150);
    } catch (e) {
      setLastScan({ ok: false, error: e?.response?.data?.detail || "Erreur" });
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
    } finally { setBusy(false); setManualToken(""); }
  };

  const logout = () => { staffAuth.clear(); setCtx(null); };

  if (!ctx) {
    return (
      <div className="max-w-md mx-auto px-4 py-8">
        <h1 className="text-2xl font-display text-center mb-4">Serveur — Connexion</h1>
        <form onSubmit={login} className="space-y-3">
          <Input data-testid="staff-event-id" placeholder="ID événement" value={eventId} onChange={(e) => setEventId(e.target.value)} />
          <Input data-testid="staff-pin" placeholder="Code PIN" value={pin} onChange={(e) => setPin(e.target.value)} type="password" />
          <Button data-testid="staff-login" type="submit" disabled={busy} className="w-full bg-[#E5276A] hover:bg-[#C01661]">
            {busy ? "…" : "Se connecter"}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-900 text-white">
      <div className="max-w-lg mx-auto px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs opacity-60">{ctx.name || "Serveur"}</div>
            <div className="text-xs opacity-40 font-mono">{ctx.event_id.slice(0, 8)}…</div>
          </div>
          <Button variant="ghost" size="sm" onClick={logout}><LogOut className="w-4 h-4" /></Button>
        </div>

        <Card className="bg-neutral-800 border-neutral-700 mb-4">
          <CardContent className="p-6 text-center">
            <ScanLine className="w-16 h-16 text-[#c99] mx-auto mb-2" />
            <p className="text-sm opacity-80">Utilisez l'appareil photo de votre téléphone pour scanner le QR du participant, puis collez le contenu ici.</p>
            <button onClick={() => setShowManual(!showManual)} className="text-xs text-[#c99] mt-2 underline">
              <Type className="w-3 h-3 inline" /> {showManual ? "Cacher" : "Saisir manuellement"}
            </button>
            {showManual && (
              <div className="mt-3 space-y-2">
                <textarea
                  data-testid="manual-token-input"
                  placeholder="Collez le contenu du QR ici"
                  value={manualToken}
                  onChange={(e) => setManualToken(e.target.value)}
                  className="w-full bg-neutral-900 text-white text-xs p-2 rounded border border-neutral-700 h-24 font-mono"
                />
                <Button data-testid="scan-btn" onClick={() => doScan(manualToken)} disabled={busy} className="w-full bg-[#E5276A] hover:bg-[#C01661]">
                  {busy ? "Vérification…" : "Valider"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {lastScan && (
          <Card className={`border-2 ${lastScan.ok ? "bg-emerald-900/30 border-emerald-500" : "bg-red-900/30 border-red-500"}`}>
            <CardContent className="p-4 text-center">
              {lastScan.ok ? (
                <>
                  <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-2" />
                  <div className="text-lg font-bold">Servi</div>
                  <div className="text-sm">{lastScan.wine?.wine_ref?.name}</div>
                  <div className="text-xs opacity-70 mt-1">+{lastScan.points} pts · {lastScan.remaining} doses restantes</div>
                </>
              ) : (
                <>
                  <XCircle className="w-16 h-16 text-red-400 mx-auto mb-2" />
                  <div className="text-lg font-bold">Refusé</div>
                  <div className="text-sm">{lastScan.error}</div>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
